import type { EnumLiteralCreateOperation } from '@festina/configuration-general-types/configuration'
import { assertNotEmpty, notEmpty } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import { type ComponentProps, useMemo } from 'react'
import { useNavigate } from 'react-router'

import { AddFieldPlaceholderInput } from '../../components/addFieldPlaceholderInput/AddFieldPlaceholderInput'
import { useEnumId } from '../../hooks/useEnumId'
import { configurationItem } from '../../system/paths'
import { useNavigationParams } from '../../system/useNavigationParams'
import { useApplicationViewOperationsContext } from '../../workspace/applicationViewOperationsContext'
import { useOperationsContext } from '../../workspace/operationsContext'

export const EnumAddLiteralPlaceholderNameCell = () => {
  const translate = useTranslate()
  const enumId = useEnumId()
  const { onAddOrUpdateOperation, workspaceKey } = useOperationsContext()
  const {
    server,
    branch,
    layer: currentLayer,
    block: currentBlock,
  } = useNavigationParams()
  const navigate = useNavigate()
  const { applicationEnumDefinitions } = useApplicationViewOperationsContext()
  const configuredEnumDefinition = applicationEnumDefinitions.get(enumId)
  const usedEnumLiteralNames = useMemo(
    () =>
      (configuredEnumDefinition?.literals ?? [])
        .filter(
          (literal) =>
            literal.type === 'modeled' || literal.layer.equals(currentLayer),
        )
        .map((literal) =>
          literal.type === 'modeled'
            ? literal.literalName
            : literal.internalOperation.entity?.name,
        )
        .filter(notEmpty),
    [configuredEnumDefinition?.literals, currentLayer],
  )

  const handleSave = async ({
    createdValue,
    layer,
    block,
  }: Parameters<
    ComponentProps<typeof AddFieldPlaceholderInput>['onSave']
  >[0]) => {
    assertNotEmpty(configuredEnumDefinition)

    const newConfiguredLiteral = {
      _type: 'EnumLiteralCreateOperation',
      entity: {
        _type: 'EnumLiteralInOut',
        enumName: enumId,
        name: createdValue,
        deprecated: false,
      },
    } satisfies EnumLiteralCreateOperation

    await onAddOrUpdateOperation({
      operation: newConfiguredLiteral,
      layer,
      block,
      shouldOpenUnifiedBranchWorkspace: true,
    })

    if (!layer.equals(currentLayer) || block !== currentBlock) {
      void navigate(
        configurationItem({
          itemId: enumId,
          type: 'enumDefinition',
          layer,
          branch,
          workspaceName: workspaceKey?.name,
          server,
          block,
        }),
      )
    }
  }

  return (
    <AddFieldPlaceholderInput
      key={`${enumId}-${currentLayer.toString()}`} // Force the component to lose its internal state if the enum or layer changes
      label={translate('enumViewer.addLiteral.label')}
      description={translate('enumViewer.addLiteral.description')}
      currentValues={usedEnumLiteralNames}
      onSave={handleSave}
    />
  )
}
